import { Button, Checkbox, NumberInput, Select, TextInput } from "@mantine/core";
import { IconPlus, IconSearch, IconX } from "@tabler/icons-react";
import { useMemo, useState } from "react";

import { Modal } from "antd";
import { RFQ_PRICE_MODES } from "@/lib/rfq/constants";
import type { RfqLineInput, RfqProductOption } from "@/lib/rfq/api";
import type { RfqPriceMode } from "@/lib/rfq/types";
import { useRfqLabels } from "@/lib/rfq/labels";

interface PickedRow {
  product: RfqProductOption;
  quantity: number;
  priceMode: RfqPriceMode;
  requiredDeliveryDate: string;
  materialCert: boolean;
  targetUnitPrice: string;
  remark: string;
}

function todayInput(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function RfqAddLineModal({
  open,
  onClose,
  products,
  onAdd,
  canSeeTarget,
}: {
  open: boolean;
  onClose: () => void;
  products: RfqProductOption[];
  onAdd: (
    inputs: Omit<RfqLineInput, "seq">[],
    sourceOrderIds?: string[],
  ) => Promise<void> | void;
  canSeeTarget: boolean;
}) {
  const { t, priceMode: priceModeLabel } = useRfqLabels();
  const [keyword, setKeyword] = useState("");
  const [picked, setPicked] = useState<Record<string, PickedRow>>({});
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    const value = keyword.trim().toLowerCase();
    if (!value) return products.slice(0, 200);
    return products
      .filter(
        (product) =>
          product.name.toLowerCase().indexOf(value) >= 0 ||
          product.spec.toLowerCase().indexOf(value) >= 0 ||
          product.material.toLowerCase().indexOf(value) >= 0,
      )
      .slice(0, 200);
  }, [products, keyword]);

  const rows = Object.values(picked);

  const toggle = (product: RfqProductOption) =>
    setPicked((prev) => {
      const next = { ...prev };
      if (next[product.id]) delete next[product.id];
      else
        next[product.id] = {
          product,
          quantity: 1,
          priceMode: product.category === "material" ? "weight" : "unit",
          requiredDeliveryDate: todayInput(),
          materialCert: false,
          targetUnitPrice: "",
          remark: "",
        };
      return next;
    });

  const patch = (id: string, value: Partial<PickedRow>) =>
    setPicked((prev) =>
      prev[id] ? { ...prev, [id]: { ...prev[id], ...value } } : prev,
    );

  const submit = async () => {
    setSaving(true);
    try {
      await onAdd(
        rows.map((row) => ({
          productId: row.product.id,
          quantity: row.quantity,
          priceMode: row.priceMode,
          requiredDeliveryDate: row.requiredDeliveryDate,
          materialCert: row.materialCert,
          remark: row.remark,
          targetUnitPrice:
            row.targetUnitPrice === "" ? null : Number(row.targetUnitPrice),
        })),
      );
      setPicked({});
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const invalid = rows.some(
    (row) => !row.quantity || !row.requiredDeliveryDate,
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1000}
      footer={null}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
      title={
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-2 text-base font-medium text-gray-700">
            <IconPlus size={18} className="text-blue-600" />
            {t("rfq.addLine.title") || "新增詢價明細"}
          </span>
          <span className="text-xs font-normal text-gray-500">
            {t("rfq.addLine.description") ||
              "明細一律從既有商品選取，品名與規格由商品帶出。"}
          </span>
        </div>
      }
    >
      <div className="grid grid-cols-1 border-t border-gray-200 md:grid-cols-[360px_1fr]">
        <div className="flex max-h-[60vh] min-h-0 flex-col border-b border-gray-200 md:border-b-0 md:border-r">
          <div className="border-b border-gray-200 p-3">
            <TextInput
              value={keyword}
              onChange={(event) => setKeyword(event.currentTarget.value)}
              placeholder={t("rfq.addLine.search") || "搜尋品名 / 規格 / 材質"}
              leftSection={<IconSearch size={15} />}
              aria-label={t("rfq.addLine.search") || "搜尋品名 / 規格 / 材質"}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {filtered.length === 0 ? (
              <p className="mt-8 text-center text-sm text-gray-500">
                {t("rfq.addLine.noProduct") || "查無商品。"}
              </p>
            ) : (
              filtered.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => toggle(product)}
                  className={`mb-1 flex w-full flex-col gap-0.5 rounded-lg border px-3 py-2 text-left transition-colors ${
                    picked[product.id]
                      ? "border-blue-200 bg-blue-50"
                      : "border-transparent hover:bg-gray-50"
                  }`}
                >
                  <span className="text-sm font-medium text-gray-700">
                    {product.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    {[product.spec, product.material].filter(Boolean).join(" ・ ") ||
                      "—"}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex max-h-[60vh] min-h-0 flex-col">
          <div className="border-b border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700">
            {t("rfq.addLine.selected", {
              defaultValue: "已選 {{n}} 項",
              n: rows.length,
            })}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {rows.length === 0 ? (
              <p className="mt-8 text-center text-sm text-gray-500">
                {t("rfq.addLine.empty") || "左側點選商品後，在這裡設定數量與交期。"}
              </p>
            ) : (
              rows.map((row) => (
                <div
                  key={row.product.id}
                  className="mb-2 flex flex-col gap-2 rounded-lg border border-gray-200 p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-gray-700">
                        {row.product.name}
                      </div>
                      <div className="truncate text-xs text-gray-500">
                        {[row.product.spec, row.product.material]
                          .filter(Boolean)
                          .join(" ・ ") || "—"}
                      </div>
                    </div>
                    <button
                      type="button"
                      aria-label={`${t("rfq.addLine.remove") || "移除"} ${row.product.name}`}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
                      onClick={() => toggle(row.product)}
                    >
                      <IconX size={14} />
                    </button>
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500">
                        {t("rfq.detail.colQty") || "詢價數量"}
                      </span>
                      <NumberInput
                        size="xs"
                        className="w-24"
                        hideControls
                        min={0}
                        value={row.quantity}
                        onChange={(value) =>
                          patch(row.product.id, { quantity: Number(value) || 0 })
                        }
                        aria-label={`${row.product.name} ${t("rfq.detail.colQty") || "詢價數量"}`}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500">
                        {t("rfq.detail.colUnit") || "單位"}
                      </span>
                      <span className="flex h-[30px] items-center text-sm text-gray-600">
                        {row.product.unit || "—"}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500">
                        {t("rfq.detail.colPriceMode") || "比價模式"}
                      </span>
                      <Select
                        size="xs"
                        className="w-32"
                        data={RFQ_PRICE_MODES.map((value) => ({
                          value,
                          label: priceModeLabel(value),
                        }))}
                        value={row.priceMode}
                        onChange={(value) =>
                          value
                            ? patch(row.product.id, {
                                priceMode: value as RfqPriceMode,
                              })
                            : undefined
                        }
                        aria-label={`${row.product.name} ${t("rfq.detail.colPriceMode") || "比價模式"}`}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500">
                        {t("rfq.detail.colDueDate") || "需求交期"}
                      </span>
                      <TextInput
                        size="xs"
                        type="date"
                        className="w-36"
                        value={row.requiredDeliveryDate}
                        onChange={(event) =>
                          patch(row.product.id, {
                            requiredDeliveryDate: event.currentTarget.value,
                          })
                        }
                        aria-label={`${row.product.name} ${t("rfq.detail.colDueDate") || "需求交期"}`}
                      />
                    </div>
                    {canSeeTarget ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-gray-500">
                          {t("rfq.detail.colTargetPrice") || "目標單價"}
                        </span>
                        <NumberInput
                          size="xs"
                          className="w-28"
                          hideControls
                          min={0}
                          value={row.targetUnitPrice}
                          placeholder="—"
                          onChange={(value) =>
                            patch(row.product.id, {
                              targetUnitPrice: String(value ?? ""),
                            })
                          }
                          aria-label={`${row.product.name} ${t("rfq.detail.colTargetPrice") || "目標單價"}`}
                        />
                      </div>
                    ) : null}
                    <Checkbox
                      size="xs"
                      className="pb-1.5"
                      checked={row.materialCert}
                      label={t("rfq.detail.colMtc") || "材證"}
                      onChange={(event) =>
                        patch(row.product.id, {
                          materialCert: event.currentTarget.checked,
                        })
                      }
                    />
                  </div>
                  <TextInput
                    size="xs"
                    value={row.remark}
                    placeholder={t("rfq.detail.colNote") || "備註"}
                    onChange={(event) =>
                      patch(row.product.id, { remark: event.currentTarget.value })
                    }
                    aria-label={`${row.product.name} ${t("rfq.detail.colNote") || "備註"}`}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
        <Button variant="default" radius="md" onClick={onClose}>
          {t("rfq.addLine.cancel") || "取消"}
        </Button>
        <Button
          radius="md"
          color="blue"
          loading={saving}
          disabled={rows.length === 0 || invalid}
          onClick={submit}
        >
          {t("rfq.addLine.confirm", {
            defaultValue: "新增 {{n}} 筆",
            n: rows.length,
          })}
        </Button>
      </div>
    </Modal>
  );
}

export default RfqAddLineModal;
